import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styles from './PIEToggler.module.scss';
import { IconChevronDown, IconChevronUp } from '@justeattakeaway/pie-icons-react';
import { useApplicationUiStore } from '@src/shared/store/ui-store/application-ui-store';

export enum PIETogglerModeEnum {
  REGULAR = 'regular',
  INVERSE = 'inverse',
}

export enum PIETogglerSizeEnum {
  NARROW = 'narrow',
  WIDE = 'wide',
}

export interface PIETogglerAriaProp {
  label: string;
}

interface IPropsPieToggler {
  children: ReactNode;
  label: ReactNode;
  secondaryLabel?: ReactNode;
  id?: string;
  icon?: ReactNode;
  isOpen: Boolean;
  mode?: PIETogglerModeEnum;
  modSeparator?: Boolean;
  size?: PIETogglerSizeEnum;
  modSSR?: Boolean;
  modEmphasis?: Boolean;
  modResponsive?: Boolean;
  modPanelPadding?: Boolean;
  modTogglePadding?: Boolean;
  modChevron?: Boolean;
  aria?: PIETogglerAriaProp;
  isActive?: boolean;
  modFullBorder?: boolean;
  className?: string;
  onToggleClick?: (...args: any) => void;
  onOpen?: (...args: any) => void;
  onClose?: (...args: any) => void;
  onOpenAnimationEnd?: (...args: any) => void;
  onCloseAnimationEnd?: (...args: any) => void;
  qaSelector?: string;
  leftContent?: ReactNode;
}

const ANIMATION_DURATION = 250; // milliseconds
const ANIMATION_DELAY = 100; // milliseconds
const PRISTINE_DELAY = 1; // milliseconds

const PIEToggler = ({
  label,
  isOpen,
  children,
  modPanelPadding = true,
  modTogglePadding = true,
  modChevron = true,
  modSeparator = true,
  icon,
  onOpen,
  onClose,
  onToggleClick,
  onCloseAnimationEnd,
  onOpenAnimationEnd,
  isActive = false,
  qaSelector,
  modFullBorder = true,
  className = '',
}: IPropsPieToggler) => {
  const uniqueHash = new Date().getTime();
  const [openState, setOpenState] = useState<Boolean>(isOpen || false);
  const [toggleState, setToggleState] = useState<Boolean>(isOpen || false);
  const [isPristine, setIsPristine] = useState(true);
  const PIETogglerRef = useRef<any>(null);
  const PIETogglerContentContainerRef = useRef(null);
  const PIETogglerHeaderRef = useRef(null);
  const PIETogglerHeaderContainerRef = useRef<any>(null);
  const [setAppDomElementClientHeightRef] = useApplicationUiStore((state) => [
    state.setAppDomElementClientHeightRef,
  ]);

  const handleOpenToggler = () => {
    const contentContainer = PIETogglerContentContainerRef.current as any;
    if (!contentContainer) return;

    contentContainer.style.overflow = 'hidden';
    const content = contentContainer.firstElementChild as any;

    if (content) {
      setTimeout(() => {
        if (!isPristine) {
          contentContainer.style.height = `${content.offsetHeight}px`;
        } else {
          contentContainer.style.height = `max-content`;
        }
        setToggleState(true);
      }, ANIMATION_DELAY);

      onOpen?.({
        refs: {
          PIETogglerRef,
          PIETogglerContentContainerRef,
          PIETogglerHeaderRef,
          PIETogglerHeaderContainerRef,
        },
      });
    }
  };

  const handleCloseToggler = () => {
    const contentContainer = PIETogglerContentContainerRef.current as any;
    if (!contentContainer) return;

    contentContainer.style.height = `${contentContainer.offsetHeight}px`;

    setTimeout(
      () => {
        contentContainer.style.height = `0px`;
        setToggleState(false);
      },
      isPristine ? PRISTINE_DELAY : ANIMATION_DELAY
    );

    onClose?.({
      refs: {
        PIETogglerRef,
        PIETogglerContentContainerRef,
        PIETogglerHeaderRef,
        PIETogglerHeaderContainerRef,
      },
    });
  };

  useEffect(() => {
    const contentContainer = PIETogglerContentContainerRef.current as any;
    if (!contentContainer) return;

    if (!isPristine) {
      contentContainer.style.transition = `height ${ANIMATION_DURATION}ms`;
    }

    if (openState === true) {
      handleOpenToggler();
    } else {
      handleCloseToggler();
    }

    setIsPristine(false);
  }, [openState]);

  useEffect(() => {
    setOpenState(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (PIETogglerHeaderContainerRef.current) {
      setAppDomElementClientHeightRef({
        domElementOffsetA:
          PIETogglerHeaderContainerRef.current.clientHeight +
          PIETogglerRef.current.offsetParent.offsetTop,
      });
    }
  }, []);

  const getHeaderContainerClasses = () => {
    const classes = [
      styles.PIETogglerHeaderContainer,
      modSeparator && styles.withSeparator,
      modTogglePadding && styles.withPadding,
      modFullBorder && styles.fullBorder,
      isActive && (openState ? styles.activeViewOpen : styles.activeView),
    ];

    return classes.filter(Boolean).join(' ');
  };

  const getAllRefs = () => ({
    PIETogglerRef,
    PIETogglerContentContainerRef,
    PIETogglerHeaderRef,
    PIETogglerHeaderContainerRef,
  });

  const handleToggleClick = () => {
    if (!modChevron) return;

    const newState = !openState;
    onToggleClick?.({
      refs: getAllRefs(),
      state: newState,
    });
    setOpenState(newState);
  };

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const toggleState = target.dataset.toggle;
    const refs = getAllRefs();

    if (toggleState === 'on') {
      target.style.height = 'max-content';
      onOpenAnimationEnd?.(refs);
    } else if (toggleState === 'off') {
      onCloseAnimationEnd?.(refs);
    }
  };

  return (
    <div
      ref={PIETogglerRef}
      id={qaSelector}
      className={`${styles.PIEToggler} ${openState ? styles.isTogglerOpen : ''} ${className}`}
    >
      <div
        className={getHeaderContainerClasses()}
        ref={PIETogglerHeaderContainerRef}
        onClick={handleToggleClick}
      >
        <div className={styles.PIETogglerHeader} ref={PIETogglerHeaderRef}>
          <div className={styles.PIETogglerHeaderLabelContainer}>
            {icon}
            {label}
          </div>
          {modChevron ? (
            <>{openState ? <IconChevronUp size="m" /> : <IconChevronDown size="m" />}</>
          ) : null}
        </div>
      </div>
      <div
        className={`${styles.PIETogglerContent} pie-toggler-content-container-${uniqueHash} ${
          modPanelPadding ? styles.withPadding : ''
        }`}
        ref={PIETogglerContentContainerRef}
        data-toggle={toggleState ? 'on' : 'off'}
        onTransitionEnd={handleTransitionEnd}
      >
        {children}
      </div>
    </div>
  );
};

export default PIEToggler;
